import { useTranslation } from "react-i18next";

import { Input } from "../../../../../components/ui/uikit/input";
import { applyDigitFormatWithCaret } from "../caretPreservingDigitFormat";
import { PHONE_NUMBER_DIGITS } from "../constants";

// ---------------------------------------------------------------------------
// PhoneNumberInput
//
// A single controlled input that stores and returns a canonical digits-only
// string (0–10 digits, no separators) while displaying it auto-formatted as
// the NANP standard xxx-xxx-xxxx as the user types.
// ---------------------------------------------------------------------------

interface PhoneNumberInputProperties {
  /** Canonical digits string. May arrive formatted; non-digits are stripped internally. */
  value: string;
  onChange: (digits: string) => void;
  onBlur?: () => void;
  ariaLabel: string;
  required?: boolean;
  /** When true, aria-invalid is set on the input. */
  invalid?: boolean;
  /** When true, suppresses the "000-000-0000" format hint (e.g. Employment Verification Phone). */
  hidePlaceholder?: boolean;
  disabled?: boolean;
  /** Used as the input's id, unless overridden by FormControl's injected `id`. */
  idBase: string;
  // Injected by FormControl's Slot when wrapped in one (id wires up FormLabel's htmlFor).
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export function formatPhoneDisplay(digits: string): string {
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function formatPhoneFromRaw(raw: string): string {
  return formatPhoneDisplay(raw.replaceAll(/\D/g, "").slice(0, PHONE_NUMBER_DIGITS));
}

/**
 * Formats as NANP `xxx-xxx-xxxx` only when there are exactly
 * {@link PHONE_NUMBER_DIGITS} digits; otherwise returns the raw digit string
 * as-is. Unlike {@link formatPhoneFromRaw}, this never truncates a
 * non-canonical digit count into a fake-looking formatted number — callers
 * that compare or persist a value verbatim (e.g. a saved Employment record's
 * phone, which may predate 10-digit validation) need the true digit count
 * preserved rather than silently reformatted/truncated.
 * @param {string} raw - The raw value; non-digits are stripped.
 * @returns {string} The formatted phone, or the bare digits when the count isn't exactly 10.
 */
export function formatPhoneOrDigits(raw: string): string {
  const digits = raw.replaceAll(/\D/g, "");
  return digits.length === PHONE_NUMBER_DIGITS ? formatPhoneDisplay(digits) : digits;
}

export function PhoneNumberInput({
  value,
  onChange,
  onBlur,
  ariaLabel,
  required,
  invalid,
  disabled,
  idBase,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  hidePlaceholder,
}: PhoneNumberInputProperties) {
  const { t } = useTranslation();
  const digits = value.replaceAll(/\D/g, "").slice(0, PHONE_NUMBER_DIGITS);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const formatted = applyDigitFormatWithCaret(event, formatPhoneFromRaw);
    onChange(formatted.replaceAll(/\D/g, ""));
  }

  return (
    <Input
      id={id ?? idBase}
      type="tel"
      inputMode="numeric"
      value={formatPhoneDisplay(digits)}
      onChange={handleChange}
      onBlur={onBlur}
      disabled={disabled}
      // Native maxLength truncates the raw pasted string before onChange strips
      // non-digits, so it must cover pastes with extra punctuation (parens,
      // spaces, a leading "+1"), not just the "xxx-xxx-xxxx" display length —
      // otherwise a paste like "+1 (313) 555-0100" loses trailing digits.
      maxLength={20}
      placeholder={hidePlaceholder ? undefined : t("review.customer.numberPlaceholder.phone")}
      aria-label={ariaLabel}
      aria-required={required}
      aria-describedby={ariaDescribedBy}
      aria-invalid={invalid || ariaInvalid || undefined}
      className="w-32"
    />
  );
}
